class CustomAnimation {
  constructor(effect) {
    this._effect = effect;
    this._playState = "idle";
    this._finished = Promise.resolve();
  }

  get finished() {
    return this._finished;
  }

  get animationEffects() {
    return this._effect instanceof CustomKeyframeEffect ? [this._effect] : this._effect.animationEffects;
  }

  cancel() {
    this.animationEffects.forEach(animationEffect => animationEffect.cancel());
  }

  finish() {
    this.animationEffects.forEach(animationEffect => animationEffect.finish());
  }

  play() {
    this._playState = "running";
    this._effect.play();
    this._finished = this._effect.finished;
    this._finished.then(
      () => {
        this._playState = "finished";
      },
      rejection => {
        this._playState = "idle";
      }
    );
  }
}

class CustomKeyframeEffect {
  constructor(target, keyframes, options = {}) {
    if (target) {
      if ("Animation" in window) {
        this._animation = new Animation(new KeyframeEffect(target, keyframes, options));
      } else {
        options.fill = "forwards";
        this._animation = target.animate(keyframes, options);
        this._animation.pause();
      }
      this._animation.addEventListener("finish", () => {
        target.style.opacity = keyframes.hasOwnProperty("opacity") ? keyframes.opacity[keyframes.opacity.length - 1] : null;
        target.style.visibility = keyframes.hasOwnProperty("visibility") ? keyframes.visibility[keyframes.visibility.length - 1] : null;
      });
    }
  }

  get finished() {
    return this._animation ? (this._animation.finished ? this._animation.finished : new Promise(resolve => this._animation.onfinish = resolve)) : Promise.resolve();
  }

  play() {
    if (this._animation) {
      this._animation.startTime = null;
      this._animation.play();
    }
  }

  cancel() {
    if (this._animation) this._animation.cancel();
  }

  finish() {
    if (this._animation) this._animation.finish();
  }
}

class GroupEffect {
  constructor(childrenEffects) {
    this._childrenEffects = childrenEffects;
    this._finished = Promise.resolve();
  }

  get finished() {
    return this._finished;
  }

  get animationEffects() {
    return this._childrenEffects.flatMap(effect => effect instanceof CustomKeyframeEffect ? effect : effect.animationEffects);
  }
}

class ParallelEffect extends GroupEffect {
  play() {
    const promises = [];
    for (const effect of this._childrenEffects) {
      effect.play();
      promises.push(effect.finished);
    }
    this._finished = Promise.all(promises);
  }
}

class SequenceEffect extends GroupEffect {
  play() {
    this._finished = new Promise(async (resolve, reject) => {
      try {
        for (const effect of this._childrenEffects) {
          effect.play();
          await effect.finished;
        }
        resolve();
      } catch {
        reject();
      }
    });
  }
}

class VideoHeroAnimation extends HTMLElement {
  constructor() {
    super();
    this.style.visibility = 'hidden';
    this._setupVisibility();
  }

  async _setupVisibility() {
    await this._untilVisible();
    const animationType = this.dataset.animationType || 'sweep';
    if (animationType !== 'none') {
      await this.transitionToEnter(animationType);
    } else {
      this.style.visibility = 'visible';
    }
  }

  _untilVisible() {
    return new Promise(resolve => {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          resolve();
        }
      });

      observer.observe(this);
    });
  }

  _getAnimationDuration() {
    const speed = this.dataset.animationSpeed || 'normal';
    const durations = {
      'very-slow': 1500,
      'slow': 1000,
      'normal': 500,
      'fast': 300,
      'very-fast': 150
    };
    return durations[speed] || 500;
  }

  async transitionToEnter(transitionType, shouldAnimate = true, reverseDirection = false) {
    this.style.visibility = 'visible';
    const duration = this._getAnimationDuration();

    let animation;
    const textElements = Array.from(this.querySelectorAll('.video-hero__title, .video-hero__subtitle, .video-hero__button'));

    switch (transitionType) {
      case 'sweep':
        animation = new CustomAnimation(new CustomKeyframeEffect(this, {
          visibility: ['hidden', 'visible'],
          clipPath: reverseDirection 
            ? ['inset(0 100% 0 0)', 'inset(0 0 0 0)'] 
            : ['inset(0 0 0 100%)', 'inset(0 0 0 0)']
        }, {
          duration: duration,
          easing: 'cubic-bezier(1, 0, 0, 1)'
        }));
        break;

      case 'fade':
        animation = new CustomAnimation(new CustomKeyframeEffect(this, {
          opacity: [0, 1],
          visibility: ['hidden', 'visible']
        }, {
          duration: duration,
          easing: 'ease-in-out'
        }));
        break;

      case 'reveal':
        animation = new CustomAnimation(new SequenceEffect([
          new CustomKeyframeEffect(this, {
            visibility: ['hidden', 'visible'],
            clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0 0)']
          }, {
            duration: duration,
            easing: 'cubic-bezier(0.5, 0.06, 0.01, 0.99)'
          }),
          new ParallelEffect(textElements.map((item, index) => 
            new CustomKeyframeEffect(item, {
              opacity: [0, 1],
              visibility: ['hidden', 'visible'],
              clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0 0)'],
              transform: ['translateY(100%)', 'translateY(0)']
            }, {
              duration: Math.min(450, duration),
              delay: 100 * index,
              easing: 'cubic-bezier(0.5, 0.06, 0.01, 0.99)'
            })
          ))
        ]));
        break;

      default:
        this.style.visibility = 'visible';
        return;
    }

    return shouldAnimate ? animation.play() : animation.finish();
  }
}

customElements.define('video-hero', VideoHeroAnimation); 